/**
 * App-level shared composer hook.
 *
 * All our chats use the same upload service and file validation.
 * Only the stream/thread differs. This hook is used by brainstorm,
 * website, ads, etc. via Chat.Root's useComposer prop.
 *
 * File uploads go through the shared `uploadFile` pipeline — same
 * resize semaphore, Sentry breadcrumbs, and error reporting as every
 * other upload path in the app.
 */
import { useMemo, useRef } from "react";
import { createComposer } from "@launch10/use-langgraph-stream";
import { UploadsAPIService } from "@rails_api_base";
import { validateFile } from "~/types/attachment";
import { uploadFile } from "~/lib/uploadPipeline";
import { useJwt, useRootPath } from "~/stores/sessionStore";
import { useWebsiteId } from "~/stores/projectStore";

export function useAppComposer() {
  const jwt = useJwt();
  const rootPath = useRootPath();

  // Read the current websiteId at upload time via a ref so the value
  // is captured fresh on each file drop. The composer is memoized and
  // persists across renders; the brainstorm flow creates the website
  // mid-conversation, so websiteId starts null and flips to a number
  // partway through. Reading via a ref means uploads after that point
  // pick up the new id without rebuilding the composer (which would
  // tear down attach state mid-flight).
  const websiteId = useWebsiteId();
  const websiteIdRef = useRef(websiteId);
  websiteIdRef.current = websiteId;

  return useMemo(() => {
    const uploadService = new UploadsAPIService({
      jwt: jwt ?? "",
      baseUrl: rootPath ?? "",
    });

    return createComposer({
      attachments: {
        upload: async (file: File) => {
          // Pass websiteId when known. On the very first brainstorm
          // turn (no project yet) it's null; Rails skips WebsiteUpload
          // creation and Tier 0's `pendingUploadUrls` path handles the
          // attachment at `BrainstormAPIService.create` time. From the
          // second turn onward the website exists and we send the id
          // so the WebsiteUpload join row is created immediately.
          const response = await uploadFile(uploadService, file, {
            isLogo: false,
            websiteId: websiteIdRef.current ?? undefined,
          });
          // `upload_id` rides through additional_kwargs.metadata.attachments
          // so the agent can link a pasted image to its `uploads` row
          // without an MCP search round-trip (see messageMetadataReminder).
          return {
            url: response.url,
            meta: {
              filename: response.filename,
              upload_id: response.id,
            },
          };
        },
        validate: validateFile,
      },
    });
  }, [jwt, rootPath]);
}
